/**
 * The "+" quick-create menu.
 *
 * CLAUDE.md §11: quick-create "opens inline drawers — never lose page context".
 * So the drawers are hosted by the app shell and opened through this module-level
 * switchboard; nothing navigates, and the list or dashboard behind the drawer
 * keeps its scroll position, its filters and its selection.
 *
 * Each entry declares the permission that makes it real, so the menu and the
 * palette's "Create" section filter identically without repeating the rule.
 */

import { ref, type Ref } from 'vue'

import type { AppIconName } from '@/lib/icons'
import { PERMISSIONS } from '@/lib/permissions'
import { onSessionEnd } from '@/lib/sessionReset'

export type QuickCreateTarget = 'invite-user'

export interface QuickCreateAction {
  id: QuickCreateTarget
  /** Message keys, not words — this is module-level data evaluated at import. */
  labelKey: string
  descriptionKey: string
  icon: AppIconName
  permission: string
  shortcut?: string
}

export const QUICK_CREATE_ACTIONS: readonly QuickCreateAction[] = [
  {
    id: 'invite-user',
    labelKey: 'quickCreate.inviteUser.label',
    descriptionKey: 'quickCreate.inviteUser.description',
    icon: 'mail',
    permission: PERMISSIONS.userInvite,
  },
]

const active = ref<QuickCreateTarget | null>(null)

/*
 * A drawer left open is not the next person's to find: the shell re-mounts it
 * from this flag on the next sign-in, so it must not survive the session that
 * opened it (`lib/sessionReset.ts`).
 */
onSessionEnd(() => {
  active.value = null
})

export interface QuickCreate {
  active: Ref<QuickCreateTarget | null>
  open: (target: QuickCreateTarget) => void
  close: () => void
  isOpen: (target: QuickCreateTarget) => boolean
}

export function useQuickCreate(): QuickCreate {
  return {
    active,
    open: (target) => {
      active.value = target
    },
    close: () => {
      active.value = null
    },
    isOpen: (target) => active.value === target,
  }
}
